import React, {HTMLProps, ReactElement, useState} from 'react';
import axios from 'axios';
import Button from '@/components/ui/Button';
import {Tooltip} from '@/components/tooltips';
import {useStoreActions, useStoreState} from 'store';
import {useAlert, ModalAlert} from '@/components/alerts';
import {shareStory} from '@/views/stories/api/stories';
import {StoryboardAccessType} from 'entities/storyboard';
import {buildStoryURL} from '@/utils/urlBuilder';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import CellLoader from '@/components/loading/CellLoader';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {extractErrorMessage} from '@/utils/errorHandling';


interface Props {
    storyId: number | null;
}

interface BulkRemoveAccessPostData {
    userIds: number[];
}

const StoryUserAccessTable = React.memo<Props>(({storyId}) => {
    const {showError} = useAlert();
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [revokeConfirmOpen, setRevokeConfirmOpen] = useState<boolean>(false);
    const [usersToRevoke, setUsersToRevoke] = useState<number[]>([]);
    const [isRevoking, setIsRevoking] = useState<boolean>(false);

    const stories = useStoreState((state) => state.stories.items);
    const deleteStoryAccess = useStoreActions((actions) => actions.stories.deleteStoryAccess);
    const editStoryAccess = useStoreActions((actions) => actions.stories.editStoryAccess);

    const activeStoryIndex = stories.findIndex((s) => s.id === storyId);
    const activeStory = activeStoryIndex !== -1 ? stories[activeStoryIndex] : null;
    const currentUserId = userData?.id;

    // Unused permission flag kept for potential future UI/logic changes
    const userCanDeleteStoryPerm = hasPermission('stories.user_access.delete', userData?.permissions, true);

    function IndeterminateCheckbox({
        indeterminate,
        className = '',
        ...rest
    }: {indeterminate?: boolean} & HTMLProps<HTMLInputElement>) {
        const ref = React.useRef<HTMLInputElement>(null!)

        React.useEffect(() => {
            if (typeof indeterminate === 'boolean') {
                ref.current.indeterminate = !rest.checked && indeterminate
            }
        }, [ref, indeterminate])

        return (
            <input
                type="checkbox"
                ref={ref}
                className={className + ' cursor-pointer'}
                {...rest}
            />
        )
    }

    const visibleUsers = activeStory?.visibleTo?.filter((user) => user.id !== currentUserId) || [];

    const accessTypeI18nKeyByCode: Record<string, string> = {
        read: 'jt_access_view.text',
        admin: 'jt_access_edit_and_delete.text',
        partial_read: 'jt_access_view_storyboards.text',
        partial_write: 'jt_access_edit_storyboards.text',
        no_access: 'jt_no_access_explanation.text',
    };

    // Early return if userData is not loaded yet - AFTER all hooks are defined
    if (loadingUserData || !userData) {
        return <CellLoader fullHeight={true} />;
    }

    if (activeStoryIndex === -1 || !activeStory) {
        return null;
    }

    // Selection helpers
    const allSelectableIds = visibleUsers.map((u) => u.id);
    const allSelected = selectedUsers.length > 0 && selectedUsers.length === allSelectableIds.length;
    const someSelected = selectedUsers.length > 0 && selectedUsers.length < allSelectableIds.length;

    const toggleAll = () => {
        if (allSelected) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(allSelectableIds);
        }
    };

    const toggleOne = (id: number) => {
        setSelectedUsers((prev) => (
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        ));
    };

    const deleteBulk = async () => {
        if (!storyId) {
            showError(i18n.t('jt_story_user_access_table_something_went_wrong.text'));
            throw Error('Cannot remove access from story without storyId');
        }


        setUsersToRevoke(selectedUsers);
        setRevokeConfirmOpen(true);
    };

    const closeRevokeConfirm = (): void => {
        setUsersToRevoke([]);
        setRevokeConfirmOpen(false);
        setIsRevoking(false);
    };

    const confirmRevoke = async (): Promise<void> => {
        // Safety check – we shouldn't get here without a story or selected users
        if (!storyId || usersToRevoke.length === 0) {
            return;
        }

        setIsRevoking(true);
        try {
            const url = buildStoryURL('removeStoryPermBulk', storyId);
            await axios.post(url, {userIds: usersToRevoke});

            // Optimistically update local store to reflect removed users
            usersToRevoke.forEach((userId) => deleteStoryAccess([storyId, userId]));
            setSelectedUsers([]);
            closeRevokeConfirm();
        } catch (error) {
            setIsRevoking(false);
            console.error('Failed to revoke story access', error);
            const errorMessage = extractErrorMessage(error, i18n.t('jt_story_user_access_table_something_went_wrong.text'));
            showError(errorMessage);
        }
    };

    const giveWriteAccess = async () => {
        if (!storyId) {
            showError(i18n.t('ch_something_went_wrong.text'));
            throw Error(i18n.t('story_user_access_table_cannot_give_access_without_story_id.text'));
        }

        const formData = {
            users: selectedUsers.map((value) => ({value})),
            accessType: {
                label: i18n.t('jt_access_edit_and_delete.text'),
                value: StoryboardAccessType.Write,
            },
        };
        const response = await shareStory(storyId, formData);
        editStoryAccess([storyId, response.data.visibleTo]);
        setSelectedUsers([]);
    };

    const deleteAccessButtonDisabled = !selectedUsers.length;

    // Use a Set to speed up membership checks when many users are selected
    const selectedUserIdsSet = new Set(selectedUsers);
    const hasNonAdminSelected = activeStory?.visibleTo
        ? activeStory.visibleTo.some(
            (user) => user.accessTypeCode !== 'admin' && selectedUserIdsSet.has(user.id),
        )
        : false;

    const giveWriteAccessButtonDisabled = !hasNonAdminSelected;

    let deleteAccessButton = (
        <span className="d-inline-block float-right mt-2 ml-2">
            <Button
                variant="danger"
                disabled={deleteAccessButtonDisabled}
                onClick={deleteBulk}
                style={{pointerEvents: deleteAccessButtonDisabled ? 'none' : 'unset'}}
                data-cy="story-user-access-table-user-accesstypecode-btn">
                {i18n.t('jt_revoke_access.text')}
            </Button>
        </span>
    );
    let giveWriteAccessButton: ReactElement | null = (
        activeStory?.userIsAdmin ? (
            <span className="d-inline-block float-right mt-2">
                <Button
                    variant="success"
                    disabled={giveWriteAccessButtonDisabled}
                    onClick={giveWriteAccess}
                    style={{pointerEvents: giveWriteAccessButtonDisabled ? 'none' : 'unset'}}
                    data-cy="story-user-access-table-success-btn">
                    {i18n.t('jt_give_edit.text')}
                </Button>
            </span>
        ) : null
    );

    if (deleteAccessButtonDisabled) {
        deleteAccessButton = (
            <Tooltip
                content={i18n.t('jt_select_user.text')}
            >
                {deleteAccessButton}
            </Tooltip>
        );
    }

    if (giveWriteAccessButtonDisabled && giveWriteAccessButton) {
        giveWriteAccessButton = (
            <Tooltip
                content={i18n.t('jt_select_less.text')}
            >
                {giveWriteAccessButton}
            </Tooltip>
        );
    }

    const noDataIndication = i18n.t('jt_no_data_available.text');

    if (!loadingUserData) {
        return (
            <>
                <table className="table table-hover table-bordered g-table w-100">
                    <thead>
                        <tr>
                            <th className="border-right">
                                <IndeterminateCheckbox
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    onChange={toggleAll}
                                />
                            </th>
                            <th className="border-right">{i18n.t('th_username_email.text')}</th>
                            <th>{i18n.t('th_access_type.text')}</th>
                        </tr>
                    </thead>
                    {
                        visibleUsers.length === 0 && (
                            <tbody>
                                <tr>
                                    <td colSpan={3}>
                                        {noDataIndication}
                                    </td>
                                </tr>
                            </tbody>
                        )
                    }
                    <tbody>
                        {visibleUsers.map((user) => (
                            <tr key={user.id}>
                                <td>
                                    <input
                                        type="checkbox"
                                        className="cursor-pointer"
                                        checked={selectedUsers.includes(user.id)}
                                        onChange={() => toggleOne(user.id)}
                                    />
                                </td>
                                <td>{user.username}</td>
                                <td>
                                    {accessTypeI18nKeyByCode[user.accessTypeCode]
                                        ? i18n.t(accessTypeI18nKeyByCode[user.accessTypeCode])
                                        : user.accessType}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {deleteAccessButton}

                {giveWriteAccessButton}
                <ModalAlert
                    isOpen={revokeConfirmOpen}
                    onClose={closeRevokeConfirm}
                    variant="warning"
                    title={i18n.t('jt_confirm_action.text')}
                    message={i18n.t('jt_st_revoke_permissions_confirm.text', {count: usersToRevoke.length, plural: usersToRevoke.length === 1 ? '' : 's'})}
                    actions={[
                        {
                            label: i18n.t('jt_revoke.text'),
                            onClick: confirmRevoke,
                            variant: 'danger',
                            loading: isRevoking
                        }
                    ]}
                    dismissible={!isRevoking}
                />
            </>
        );
    } else {
        return (
            <CellLoader />
        );
    }
});

export default StoryUserAccessTable;
